import Link from "next/link";

import { DetailSidebarType } from "@src/features/sidebars/detailSidebar";
import TriangleWarningOutlineIcon from "@src/lib/icons/18px/triangle-warning-outline";
import Banner from "@src/lib/ui/Banner";
import { pluralize } from "@src/lib/utils/strings";

import {
  type OrgGuestFeedbackIntegrationFragment,
  useOrgGuestFeedbackIntegrationsQuery,
} from "./GuestFeedbackIntegrationsTab.generated";

const getIntegrationType = (
  integration: OrgGuestFeedbackIntegrationFragment,
): string => {
  if (integration.googleBusinessConfig) return "Google Business";
  if (integration.ovationIntegrationConfig) return "Ovation";
  if (integration.tattleIntegrationConfig) return "Tattle";
  return "Unknown";
};

const UnmatchedIntegrationLocationsBanner: React.FC = () => {
  const { data } = useOrgGuestFeedbackIntegrationsQuery();
  const integrations = data?.OrgGuestFeedbackIntegrations ?? [];
  const integrationsWithUnmatchedLocations = integrations
    .map((integration) => ({
      integration,
      unmatchedCount: integration.locations.filter(({ location }) => !location)
        .length,
    }))
    .filter(({ unmatchedCount }) => unmatchedCount > 0);

  if (integrationsWithUnmatchedLocations.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      {integrationsWithUnmatchedLocations.map(
        ({ integration, unmatchedCount }) => (
          <IntegrationBanner
            key={integration.id}
            integration={integration}
            unmatchedCount={unmatchedCount}
          />
        ),
      )}
    </div>
  );
};

const IntegrationBanner: React.FC<{
  integration: OrgGuestFeedbackIntegrationFragment;
  unmatchedCount: number;
}> = ({ integration, unmatchedCount }) => {
  const provider = getIntegrationType(integration);
  const verb = unmatchedCount === 1 ? "isn't" : "aren't";
  const message = `${unmatchedCount} ${pluralize("location", unmatchedCount)} from ${provider} ${verb} linked to your stores. Recommendations for their reviews can't be targeted until they are.`;

  return (
    <Banner
      Icon={TriangleWarningOutlineIcon}
      className="bg-status-warning-bg text-status-warning-text"
    >
      {message}{" "}
      <Link
        href={{
          pathname: "/guest-feedback",
          query: {
            tab: "data-sources",
            objectId: integration.id,
            sidebarType: DetailSidebarType.GuestFeedbackIntegration,
            tableId: "guest-feedback-integrations",
            integrationTab: "locations",
          },
        }}
        className="font-semibold underline"
      >
        Link locations
      </Link>
    </Banner>
  );
};

export default UnmatchedIntegrationLocationsBanner;
